import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { customersApi, type CustomerPayload, type PartnerCategory } from '@/api/customersApi'
import { useTenant } from '@/providers/TenantProvider'

/** Prefix key for every customers query, for invalidation. */
const CUSTOMERS_KEY = ['customers'] as const

/**
 * The selected tenant's partners (z8nrz7earp), ordered by code. The list is
 * small and fetched whole — search and column sort happen client-side. The
 * tenant is in the key so a switch can never paint another tenant's rows.
 *
 * `categories` narrows it server-side: the customer pickers ask for
 * `['Customer']`, which is the list of names a customer is chosen from.
 */
export function useCustomersList({
  enabled = true,
  categories,
}: { enabled?: boolean; categories?: readonly PartnerCategory[] } = {}) {
  const { selectedTenantId } = useTenant()
  return useQuery({
    queryKey: [...CUSTOMERS_KEY, 'list', selectedTenantId, categories?.join(',') ?? 'all'],
    enabled: enabled && !!selectedTenantId,
    queryFn: () => customersApi.list({ categories }),
  })
}

/** Create / update / delete, each refreshing the list on success. */
export function useCustomerMutations() {
  const qc = useQueryClient()
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: CUSTOMERS_KEY })
  }
  const create = useMutation({
    mutationFn: (payload: CustomerPayload) => customersApi.create(payload),
    onSuccess: invalidate,
  })
  const update = useMutation({
    mutationFn: (input: { id: string; payload: CustomerPayload }) =>
      customersApi.update(input.id, input.payload),
    onSuccess: invalidate,
  })
  const remove = useMutation({
    mutationFn: (id: string) => customersApi.remove(id),
    onSuccess: invalidate,
  })
  return { create, update, remove }
}
